import { z } from 'zod';
/**
 * Contratos do conteúdo editável do site.
 *
 * Cada seção do site institucional é uma linha em `site_content`, guardada como
 * JSON e validada por um schema próprio — o mapa `SECTION_SCHEMAS` no fim deste
 * arquivo é o que liga a chave da seção ao seu formato.
 *
 * A escolha de guardar JSON em vez de uma tabela por seção está registrada em
 * `docs/decisoes/0013-conteudo-do-site-em-uma-tabela.md`. O resumo é que a
 * garantia de tipo vem do Zod na borda, que é como o projeto já valida tudo o
 * que entra, e o preço de uma coluna menos explícita compra uma migration só
 * para as sete seções.
 */
export const coordenadasSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
});
/**
 * Ponto encontrado a partir de um endereço escrito.
 *
 * A `descricao` é o endereço como o serviço o entendeu, e existe para ser lida
 * por quem buscou: geocodificação erra, e conferir "é este mesmo?" antes de
 * salvar é a diferença entre um mapa certo e um cliente dirigindo para o lugar
 * errado.
 *
 * `aproximado` diz que o serviço achou a rua, mas não o número da porta — o
 * ponto é o meio da via. É o caso comum no Brasil, onde poucos imóveis estão
 * mapeados individualmente, e muda o que se deve conferir no mapa: a quadra
 * certa, não o alfinete exato.
 */
export const enderecoEncontradoSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    descricao: z.string(),
    aproximado: z.boolean(),
});
export const redeSocialSchema = z.object({
    rotulo: z.string().trim().min(1).max(40),
    url: z.url().max(300),
});
/** Só dígitos, com DDI e DDD — é o formato que o `wa.me` e o `tel:` aceitam. */
const numeroDiscavel = z
    .string()
    .trim()
    .regex(/^\d{10,15}$/, 'Use só os dígitos, com DDI e DDD. Exemplo: 5545990001420.');
/**
 * Contato da marcenaria, usado no rodapé e na seção do showroom.
 *
 * Formato achatado de propósito: cada campo vira um input da tela do painel sem
 * nenhuma tradução no meio.
 */
export const contatoSchema = z.object({
    empresa: z.string().trim().min(1).max(80),
    telefoneExibicao: z.string().trim().min(1).max(40),
    telefoneDigitos: numeroDiscavel,
    /**
     * O WhatsApp costuma ser um celular diferente do telefone fixo. São dois
     * campos porque já foram confundidos uma vez: um link de `wa.me` apontando
     * para linha fixa abre uma conversa que não chega em ninguém.
     */
    whatsappExibicao: z.string().trim().min(1).max(40),
    whatsappDigitos: numeroDiscavel,
    email: z.email().max(190),
    enderecoLinha1: z.string().trim().min(1).max(120),
    enderecoLinha2: z.string().trim().min(1).max(120),
    /** Por extenso na seção do showroom; abreviado na coluna estreita do rodapé. */
    horarioSemana: z.string().trim().min(1).max(80),
    horarioSabado: z.string().trim().max(80),
    horarioSemanaCurto: z.string().trim().min(1).max(40),
    horarioSabadoCurto: z.string().trim().max(40),
    /**
     * `null` esconde o mapa e o botão de rota.
     *
     * É proposital: um mapa apontando para o lugar errado manda alguém dirigir
     * até o endereço errado, o que é pior do que não ter mapa.
     */
    coordenadas: coordenadasSchema.nullable(),
    cnpj: z.string().trim().max(30),
    redes: z.array(redeSocialSchema).max(6),
});
/**
 * Título de seção com quebra de linha e uma palavra em destaque.
 *
 * As cinco seções seguem o mesmo desenho — "Nenhuma *terceirizada*.", "não uma
 * *revenda* de catálogo." — então em vez de quebrar cada título em três campos
 * rígidos, o texto carrega duas marcas: quebra de linha é quebra de linha, e
 * `*assim*` vira o destaque em itálico da cor da marca.
 *
 * É a menor convenção que resolve os cinco casos. Um editor de texto rico aqui
 * seria a versão cara do mesmo problema, e daria ao cliente liberdade que o
 * desenho da seção não comporta.
 */
export const tituloComDestaqueSchema = z.string().trim().min(1).max(200);
export const etapaSchema = z.object({
    titulo: z.string().trim().min(1).max(80),
    descricao: z.string().trim().min(1).max(500),
    /** Tempo estimado, como "20 a 25 dias". Pode ficar vazio. */
    prazo: z.string().trim().max(60),
});
/**
 * Seção do processo de atendimento.
 *
 * A numeração visível não é campo: sai da posição na lista. Deixá-la editável
 * criaria a possibilidade de duas etapas "02" e de a ordem contradizer o número
 * — e não existe caso de uso para numerar fora de sequência.
 */
export const processoSchema = z.object({
    kicker: z.string().trim().min(1).max(60),
    titulo: tituloComDestaqueSchema,
    etapas: z.array(etapaSchema).min(1).max(8),
});
/** Âncoras que existem no site. Fechada de propósito: link do banner não pode apontar para o vazio. */
export const ancoraSchema = z.enum([
    '#atelie',
    '#ambientes',
    '#projetos',
    '#processo',
    '#showroom',
    '#orcamento',
]);
export const chamadaSchema = z.object({
    rotulo: z.string().trim().min(1).max(40),
    ancora: ancoraSchema,
});
/**
 * Identificador de vídeo do YouTube — os onze caracteres, não a URL inteira.
 *
 * O painel aceita a URL colada e extrai o identificador antes de enviar; aqui
 * só o identificador é válido, para não haver duas formas do mesmo dado.
 */
export const youtubeIdSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{11}$/, 'Identificador de vídeo do YouTube inválido.');
/**
 * Maior lado de uma imagem de fundo, depois do redimensionamento no navegador.
 *
 * Maior que o teto da galeria (1600) porque o banner ocupa a tela inteira: uma
 * imagem de 1600 esticada para 1920 fica visivelmente mole, e é a primeira
 * coisa que alguém vê do site. O teto de bytes continua o mesmo.
 */
export const IMAGE_MAX_DIMENSION_FUNDO = 2400;
/** Identificador de uma imagem em `media`. */
export const mediaIdSchema = z.string().trim().min(1).max(30);
export const mediaSchema = z.object({
    id: z.string(),
    /** Caminho da rota que serve os bytes. */
    url: z.string(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
/**
 * Fundo do banner.
 *
 * União discriminada, e não três campos que podem se contradizer: com
 * `youtubeId` e `imagemId` soltos no mesmo objeto, existiria o estado "os dois
 * preenchidos", e alguém teria de escolher a regra de desempate em tempo de
 * renderização. Aqui o estado inválido não é representável.
 */
export const fundoDoHeroSchema = z.discriminatedUnion('tipo', [
    z.object({ tipo: z.literal('solido') }),
    z.object({ tipo: z.literal('video'), youtubeId: youtubeIdSchema }),
    z.object({ tipo: z.literal('imagem'), mediaId: mediaIdSchema }),
]);
/** Banner do topo. */
export const heroSchema = z.object({
    kicker: z.string().trim().min(1).max(60),
    titulo: tituloComDestaqueSchema,
    descricao: z.string().trim().min(1).max(400),
    chamadaPrincipal: chamadaSchema,
    chamadaSecundaria: chamadaSchema,
    /** Argumentos de venda da faixa embaixo. Lidos por leitor de tela. */
    numeros: z.array(z.string().trim().min(1).max(40)).max(4),
    fundo: fundoDoHeroSchema,
});
export const estatisticaSchema = z.object({
    valor: z.number().int().min(0).max(1_000_000),
    /** Sinal depois do número, como o "+" de "+940". Pode ficar vazio. */
    sufixo: z.string().trim().max(4),
    rotulo: z.string().trim().min(1).max(40),
});
/** Seção do ateliê. */
export const atelieSchema = z.object({
    kicker: z.string().trim().min(1).max(60),
    titulo: tituloComDestaqueSchema,
    paragrafos: z.array(z.string().trim().min(1).max(800)).min(1).max(4),
    selos: z.array(z.string().trim().min(1).max(40)).max(8),
    /** Foto larga, no alto. `null` deixa o quadro vazio, com a proporção certa. */
    fotoPrincipal: mediaIdSchema.nullable(),
    /** Cota escrita sobre a foto principal. Some junto com ela. */
    cota: z.string().trim().max(40),
    /** Foto em pé, embaixo. */
    fotoSecundaria: mediaIdSchema.nullable(),
    numeros: z.array(estatisticaSchema).max(6),
});
export const ambienteSchema = z.object({
    nome: z.string().trim().min(1).max(60),
    detalhe: z.string().trim().max(120),
    /** Miniatura que acompanha o cursor. Sem ela, a linha só acende. */
    foto: mediaIdSchema.nullable(),
});
/** Seção dos ambientes atendidos. */
export const ambientesSchema = z.object({
    kicker: z.string().trim().min(1).max(60),
    titulo: tituloComDestaqueSchema,
    /** Dica no canto do cabeçalho. Some em quem não tem cursor. */
    dica: z.string().trim().max(40),
    /** Para onde cada linha leva. Uma só, porque todas levam ao mesmo lugar. */
    ancora: ancoraSchema,
    itens: z.array(ambienteSchema).min(1).max(12),
});
/**
 * Seção do showroom.
 *
 * Endereço, horário, telefone e coordenadas **não** estão aqui: vêm de
 * `contato`, que é a fonte única desses dados. Duplicá-los faria o rodapé e o
 * showroom divergirem no dia em que a marcenaria mudasse de endereço.
 */
export const showroomSchema = z.object({
    kicker: z.string().trim().min(1).max(60),
    titulo: tituloComDestaqueSchema,
    lead: z.string().trim().min(1).max(600),
    /** Etiqueta escrita sobre o mapa. */
    etiquetaDoMapa: z.string().trim().max(60),
    foto: mediaIdSchema.nullable(),
});
/**
 * Depoimento de cliente.
 *
 * Texto vazio esconde a seção inteira, e isso existe por um motivo concreto: o
 * depoimento que veio do protótipo é de demonstração, e publicar como real um
 * elogio que ninguém escreveu é inventar cliente. Enquanto não houver um de
 * verdade, com autorização de quem falou, é melhor não ter seção nenhuma.
 *
 * Com texto, o autor passa a ser obrigatório: citação sem quem a disse não
 * convence ninguém e ainda parece inventada.
 */
export const depoimentoSchema = z
    .object({
    texto: z.string().trim().max(600),
    autor: z.string().trim().max(60),
    contexto: z.string().trim().max(120),
})
    .refine((d) => d.texto === '' || d.autor !== '', {
    message: 'Diga quem falou, ou deixe o depoimento vazio para esconder a seção.',
    path: ['autor'],
});
export const siteSectionSchema = z.enum([
    'ambientes',
    'atelie',
    'contato',
    'depoimento',
    'hero',
    'processo',
    'showroom',
]);
/**
 * Formato de cada seção.
 *
 * O `Record<SiteSection, ...>` é o ponto do desenho: acrescentar uma chave em
 * `siteSectionSchema` sem escrever o schema dela vira erro de compilação, em
 * vez de uma seção que aceita qualquer coisa em produção.
 */
export const SECTION_SCHEMAS = {
    ambientes: ambientesSchema,
    atelie: atelieSchema,
    contato: contatoSchema,
    depoimento: depoimentoSchema,
    hero: heroSchema,
    processo: processoSchema,
    showroom: showroomSchema,
};
export const siteContentSchema = z.object({
    section: siteSectionSchema,
    data: z.unknown(),
    updatedAt: z.iso.datetime(),
});
/**
 * Conteúdo inicial do contato.
 *
 * Serve a dois propósitos: é o que o site mostra enquanto a API não responde —
 * o rodapé aparece em toda página e não pode depender de rede —, e é o que o
 * painel preenche no formulário quando a seção ainda nunca foi salva.
 *
 * ATENÇÃO — tudo aqui é dado de demonstração do protótipo, marcado como tal no
 * `LEIA-ME.md` dele: endereço, telefones, e-mail, CNPJ e redes. Nada foi
 * confirmado com a Plane, e o telefone e o e-mail já estão no ar. Trocar isto
 * pelo real é a primeira coisa a fazer no painel.
 */
export const CONTATO_PADRAO = {
    empresa: 'Plane Móveis',
    telefoneExibicao: '(45) 3000-1420',
    telefoneDigitos: '554530001420',
    whatsappExibicao: '(45) 99000-1420',
    whatsappDigitos: '5545990001420',
    email: 'contato@planemoveis.com.br',
    enderecoLinha1: 'Av. Brasil, 1420 — Bairro Industrial',
    enderecoLinha2: 'Cascavel / PR · 85806-000',
    horarioSemana: 'Segunda a sexta · 08h às 18h',
    horarioSabado: 'Sábado · 08h às 12h',
    horarioSemanaCurto: 'Seg a sex 08h–18h',
    horarioSabadoCurto: 'Sáb 08h–12h',
    coordenadas: null,
    cnpj: '00.000.000/0001-00',
    redes: [
        { rotulo: 'Instagram', url: 'https://instagram.com/' },
        { rotulo: 'Pinterest', url: 'https://pinterest.com/' },
        { rotulo: 'Facebook', url: 'https://facebook.com/' },
    ],
};
/**
 * Conteúdo inicial do processo, copiado do protótipo.
 *
 * Os prazos não estão marcados como demonstração no `LEIA-ME.md` do protótipo,
 * mas prazo publicado vira expectativa de quem contrata: confira com a Plane.
 */
export const PROCESSO_PADRAO = {
    kicker: '04 — Processo',
    titulo: 'Quatro etapas.\nNenhuma *terceirizada*.',
    etapas: [
        {
            titulo: 'Visita e medição',
            descricao: 'Vamos até você com trena a laser. Levantamos prumo, esquadro, ponto de água, tomada e altura de forro — e já conversamos sobre valores ali mesmo.',
            prazo: '1 hora · sem custo',
        },
        {
            titulo: 'Projeto 3D e ajustes',
            descricao: 'Você recebe o projeto renderizado com cada medida cotada. Ajustamos junto quantas vezes precisar — e só depois do seu aceite a chapa é cortada.',
            prazo: 'até 5 dias úteis',
        },
        {
            titulo: 'Corte e usinagem',
            descricao: 'Seccionadora CNC, coladeira de borda e conferência peça a peça. Cada módulo sai da fábrica etiquetado com o nome do ambiente onde vai morar.',
            prazo: '20 a 25 dias',
        },
        {
            titulo: 'Montagem e entrega',
            descricao: 'Equipe própria, uniformizada, com aspirador e proteção de piso. No fim, regulagem de porta e gaveta e assinatura do termo de garantia.',
            prazo: '2 a 4 dias',
        },
    ],
};
/**
 * Conteúdo inicial do banner, copiado do protótipo.
 *
 * Os três números são dado de demonstração, marcados no `LEIA-ME.md`. O fundo
 * nasce sólido: o protótipo previa um vídeo da marcenaria que nunca foi
 * entregue, e sem ele o verde da marca é o certo.
 */
export const HERO_PADRAO = {
    kicker: 'Cascavel · PR — desde 2009',
    titulo: 'Cada _milímetro_\npensado para o\n*seu* espaço.',
    descricao: 'Projetamos, usinamos e montamos móveis planejados na nossa própria fábrica. Sem intermediários, sem folga de 5\u00A0mm, sem surpresa no dia da entrega.',
    chamadaPrincipal: { rotulo: 'Quero um orçamento', ancora: '#orcamento' },
    chamadaSecundaria: { rotulo: 'Ver projetos entregues', ancora: '#projetos' },
    numeros: ['16 anos', '+940 ambientes', 'garantia de 5 anos'],
    fundo: { tipo: 'solido' },
};
/**
 * Conteúdo inicial do ateliê, copiado do protótipo.
 *
 * ATENÇÃO — o `LEIA-ME.md` marca como dado de demonstração os quatro números
 * abaixo, a história do "Seu Antônio" e a metragem da fábrica. As duas fotos
 * nascem vazias: as do protótipo vêm do Unsplash, e apresentar a oficina de
 * terceiros como sendo a fábrica da Plane seria afirmar algo falso.
 */
export const ATELIE_PADRAO = {
    kicker: '01 — O ateliê',
    titulo: 'Uma marcenaria de verdade,\nnão uma *revenda* de catálogo.',
    paragrafos: [
        'O Seu Antônio abriu a Plane em 2009 com uma esquadrejadeira usada e um galpão de 90\u00A0m². Hoje são 1.400\u00A0m², seccionadora com controle numérico e coladeira de borda — mas quem tira a medida na sua casa continua sendo quem desenha o projeto.',
        'Trabalhamos com MDF de baixa emissão, ferragens Blum e Häfele com garantia vitalícia do fabricante e acabamentos aplicados e conferidos no chão de fábrica, um a um. Nada sai daqui sem passar pela conferência de esquadro.',
    ],
    selos: [
        'MDF certificado FSC®',
        'Ferragens Blum & Häfele',
        'Usinagem CNC própria',
        'Montagem com equipe interna',
    ],
    fotoPrincipal: null,
    cota: '1.400\u00A0m² de fábrica',
    fotoSecundaria: null,
    numeros: [
        { valor: 16, sufixo: '', rotulo: 'anos de estrada' },
        { valor: 940, sufixo: '+', rotulo: 'ambientes entregues' },
        { valor: 35, sufixo: '', rotulo: 'dias, prazo médio' },
        { valor: 5, sufixo: '', rotulo: 'anos de garantia' },
    ],
};
/**
 * Conteúdo inicial dos ambientes, copiado do protótipo.
 *
 * As fotos nascem vazias, como no ateliê: as do protótipo vêm do Unsplash.
 * Sem foto a linha continua acendendo no hover — só a miniatura que acompanha
 * o cursor não aparece.
 */
export const AMBIENTES_PADRAO = {
    kicker: '02 — Ambientes',
    titulo: 'O que a gente\n*desenha* e constrói',
    dica: 'passe o mouse',
    ancora: '#orcamento',
    itens: [
        { nome: 'Cozinhas', detalhe: 'torre quente · gavetão · nichos de embutir', foto: null },
        { nome: 'Dormitórios', detalhe: 'cabeceira estofada · roupeiro de teto ao piso', foto: null },
        { nome: 'Closets', detalhe: 'perfil de alumínio · LED perfilado · sapateira', foto: null },
        { nome: 'Salas & home', detalhe: 'painel ripado · rack suspenso · estante vazada', foto: null },
        { nome: 'Home office', detalhe: 'passa-fio embutido · bancada em L · prateleira', foto: null },
        { nome: 'Gourmet & comercial', detalhe: 'churrasqueira · adega · balcão de loja', foto: null },
    ],
};
/**
 * Conteúdo inicial do showroom, copiado do protótipo.
 *
 * A foto nasce vazia, como nas outras seções. O endereço e as coordenadas — que
 * decidem se o mapa aparece — estão em `CONTATO_PADRAO`.
 */
export const SHOWROOM_PADRAO = {
    kicker: '05 — Onde a gente fica',
    titulo: 'Venha tocar\no *acabamento*.',
    lead: 'Showroom e fábrica no mesmo endereço. Você escolhe a cor com a amostra na mão, abre uma gaveta com amortecedor Blum e ainda vê a peça sendo usinada lá atrás.',
    etiquetaDoMapa: 'Plane Móveis · showroom + fábrica',
    foto: null,
};
/**
 * Conteúdo inicial do depoimento, copiado do protótipo.
 *
 * ATENÇÃO — o `LEIA-ME.md` lista "o depoimento da Marcela" entre os dados de
 * demonstração, e este é o mais delicado deles: ele convence justamente por ser
 * específico. Nome, endereço e detalhe da obra são de uma pessoa que não existe.
 * Troque por um depoimento real, com autorização de quem falou, ou apague o
 * texto para esconder a seção.
 */
export const DEPOIMENTO_PADRAO = {
    texto: 'Pedi um roupeiro para uma parede que não tinha um canto reto. Mediram três vezes, me mostraram no desenho onde o rodapé ia compensar a diferença, e na montagem encostou tudo sem um vão sequer. Foi esse detalhe que me fez indicar pra minha irmã.',
    autor: 'Marcela A.',
    contexto: 'Suíte e closet · Ed. Ipê Amarelo, Cascavel',
};
/** Valor inicial de cada seção, para o painel abrir preenchido. */
export const SECTION_DEFAULTS = {
    ambientes: AMBIENTES_PADRAO,
    atelie: ATELIE_PADRAO,
    contato: CONTATO_PADRAO,
    depoimento: DEPOIMENTO_PADRAO,
    hero: HERO_PADRAO,
    processo: PROCESSO_PADRAO,
    showroom: SHOWROOM_PADRAO,
};
//# sourceMappingURL=site-content.js.map